import { useRef, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "sonner";

import { useApiHeaders } from "@/hooks/use-api-headers";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { putToPresignedUrl } from "@/lib/upload";
import { apiError, showApiError } from "@/lib/api-error";
import type { TFunction } from "@/i18n/catalog";

const API_URL = import.meta.env.VITE_API_URL ?? "http://localhost:8000";

/** Both limits are mirrored from the API so the user hears about a 3 MB file before uploading it,
 * and the API refuses independently — the commit measures what R2 actually stored. */
const BRAND_CONTENT_TYPES = ["image/png", "image/jpeg", "image/webp"];
const MAX_BRAND_ASSET_BYTES = 512 * 1024;

/** The organisation logo: preview, upload, replace, remove (POS2-1132, shared by POS2-1146).
 *
 * **It applies as soon as a file is chosen, unlike every other field beside it.** It has to: the
 * bytes go straight to R2 and the key is then committed server-side, so there is no value a form
 * could hold and submit later. Deferring it to Save would mean an upload that has already
 * happened being "saved" afterwards, and a Cancel that looks like an undo but is not. The section
 * says so on screen instead.
 *
 * Two callers, and the two props that differ between them are the two that matter:
 *
 * * `logoPath` is the route base — `/admin/organisations/{id}/logo` for a platform admin, or
 *   `/organisations/me/logo` for an org admin acting on their own organisation. The three verbs
 *   hang off it identically, so a caller cannot reach a route it has no business calling.
 * * `t` is passed in rather than taken from `useTranslation`, because `/admin/*` is deliberately
 *   English-only (`ENGLISH_ONLY` in `eslint.config.js`) and passes its fixed `englishT`, while
 *   `/settings/organisation` passes the reader's own. This component sits under the i18n sweep;
 *   its callers do not both want to obey it.
 */
export function BrandLogoField({
  t,
  logoPath,
  initialLogoKey,
  onChanged,
}: {
  t: TFunction;
  logoPath: string;
  initialLogoKey: string | null;
  onChanged: () => void;
}) {
  const headers = useApiHeaders();
  const [logoKey, setLogoKey] = useState(initialLogoKey);
  const [rejected, setRejected] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  const upload = useMutation({
    mutationFn: async (file: File) => {
      const signed = await postLogoUploadUrl(t, logoPath, file, headers);
      await putToPresignedUrl({ url: signed.url, headers: signed.headers, file });
      return commitLogo(t, logoPath, signed.key, headers);
    },
    onSuccess: (key) => {
      setLogoKey(key);
      onChanged();
      toast.success(t("settings.logoUpdated"));
    },
    onError: showApiError,
  });

  const remove = useMutation({
    mutationFn: async () => {
      const res = await fetch(`${API_URL}/api/v1${logoPath}`, { method: "DELETE", headers });
      if (!res.ok) throw await apiError(t, res);
    },
    onSuccess: () => {
      setLogoKey(null);
      onChanged();
      toast.success(t("settings.logoRemoved"));
    },
    onError: showApiError,
  });

  const busy = upload.isPending || remove.isPending;

  function choose(file: File | undefined) {
    setRejected(null);
    if (!file) return;
    if (!BRAND_CONTENT_TYPES.includes(file.type)) {
      setRejected(t("settings.logoRejectedType"));
      return;
    }
    if (file.size > MAX_BRAND_ASSET_BYTES) {
      setRejected(
        t("settings.logoRejectedSize", {
          size: Math.round(file.size / 1024),
          max: MAX_BRAND_ASSET_BYTES / 1024,
        }),
      );
      return;
    }
    upload.mutate(file);
  }

  return (
    <div>
      <p className="text-sm font-medium">{t("settings.logo")}</p>
      {/* A drop bypasses the file input's `accept` filter, which is why the type check below
          is not redundant with it — and why the API checks again after that. */}
      <div
        data-testid="logo-dropzone"
        className="mt-1 flex items-center gap-3 rounded-md border border-input p-3"
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => {
          e.preventDefault();
          choose(e.dataTransfer.files[0]);
        }}
      >
        {logoKey ? (
          <LogoPreview t={t} logoKey={logoKey} />
        ) : (
          <span className="text-xs text-muted-foreground">{t("settings.logoNone")}</span>
        )}
        <div className="ml-auto flex items-center gap-2">
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={busy}
            onClick={() => fileInput.current?.click()}
          >
            {upload.isPending
              ? t("settings.logoUploading")
              : logoKey
                ? t("settings.logoReplace")
                : t("settings.logoUpload")}
          </Button>
          {logoKey && (
            <Button
              type="button"
              size="sm"
              variant="ghost"
              disabled={busy}
              onClick={() => remove.mutate()}
            >
              {t("settings.remove")}
            </Button>
          )}
        </div>
        <input
          ref={fileInput}
          type="file"
          className="hidden"
          aria-label={t("settings.logoFile")}
          accept={BRAND_CONTENT_TYPES.join(",")}
          onChange={(e) => {
            choose(e.target.files?.[0]);
            e.target.value = "";
          }}
        />
      </div>
      {rejected && <p className="mt-1 text-xs text-destructive">{rejected}</p>}
      <p className="mt-1 text-xs text-muted-foreground">
        {t("settings.logoHint", { max: MAX_BRAND_ASSET_BYTES / 1024 })}
      </p>
    </div>
  );
}

/** Split out so the read-URL query never has to run with a null key, and so a replacement —
 * which always lands on a new key, by construction — remounts the image against a URL nothing
 * has cached. */
function LogoPreview({ t, logoKey }: { t: TFunction; logoKey: string }) {
  const headers = useApiHeaders();
  const preview = useQuery({
    queryKey: ["images", "read-url", logoKey],
    // The signed URL lives an hour; holding it for half that keeps the `<img src>` stable
    // across renders without ever handing out one that has expired.
    staleTime: 30 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
    queryFn: async () => {
      const res = await fetch(
        `${API_URL}/api/v1/images/read-url?key=${encodeURIComponent(logoKey)}`,
        { headers },
      );
      if (!res.ok) throw await apiError(t, res);
      const json = await res.json();
      return json.data.url as string;
    },
  });

  if (preview.isError) {
    return <span className="text-xs text-destructive">{t("settings.logoLoadFailed")}</span>;
  }
  if (!preview.data) {
    return <Skeleton className="h-8 w-24" />;
  }
  return (
    <img
      src={preview.data}
      alt={t("settings.logoAlt")}
      className="max-h-8 max-w-32 object-contain"
    />
  );
}

async function postLogoUploadUrl(
  t: TFunction,
  logoPath: string,
  file: File,
  headers: Record<string, string>,
): Promise<{ key: string; url: string; headers: Record<string, string> }> {
  const res = await fetch(`${API_URL}/api/v1${logoPath}/upload-url`, {
    method: "POST",
    headers,
    body: JSON.stringify({
      filename: file.name,
      content_type: file.type,
      content_length: file.size,
    }),
  });
  if (!res.ok) throw await apiError(t, res);
  const json = await res.json();
  return json.data;
}

async function commitLogo(
  t: TFunction,
  logoPath: string,
  key: string,
  headers: Record<string, string>,
): Promise<string> {
  const res = await fetch(`${API_URL}/api/v1${logoPath}`, {
    method: "PUT",
    headers,
    body: JSON.stringify({ key }),
  });
  if (!res.ok) throw await apiError(t, res);
  const json = await res.json();
  return json.data.logo_key as string;
}
